import type { KeyboardEvent } from '../react';

type BasicKey = 'enter';
type ModifierKey = 'meta' | 'shift';
type ModifiedKey = `${BasicKey}+${ModifierKey}`;
type SupportedKeymaps = BasicKey | ModifiedKey;

type KeymapConfig = {
  debounce?: boolean | number;
  disabled?: boolean;
  key: SupportedKeymaps;
  onTrigger: () => void;
};

type EnterKeyEventConfig = {
  keymaps: KeymapConfig[];
};

export const onEnterKeyDown = (config: EnterKeyEventConfig) => {
  const timeouts = new Map<SupportedKeymaps, NodeJS.Timeout>();

  return (event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key !== 'Enter') return;

    config.keymaps.forEach((keymap) => {
      const hasShift = keymap.key.includes('shift');
      const hasMeta = keymap.key.includes('meta');
      const noModifiers = !hasShift && !hasMeta;

      if (
        (noModifiers && !event.shiftKey && !event.metaKey) ||
        (hasShift === event.shiftKey && hasMeta === event.metaKey)
      ) {
        // We still want to prevent default behavior even if the keymap is disabled
        event.preventDefault();

        if (keymap.disabled) return;

        // Handle debouncing
        if (keymap.debounce) {
          const existingTimeout = timeouts.get(keymap.key);
          if (existingTimeout) {
            clearTimeout(existingTimeout);
          }

          const debounceTime = typeof keymap.debounce === 'number' ? keymap.debounce : 200;

          timeouts.set(
            keymap.key,
            setTimeout(() => {
              keymap.onTrigger();
              timeouts.delete(keymap.key);
            }, debounceTime),
          );
        } else {
          keymap.onTrigger();
        }
      }
    });
  };
};
